<script lang="ts">
  import type { SessionUser } from '@ucc/shared';
  import { PASSWORD_MIN_LENGTH } from '@ucc/shared';
  import { ApiError, changePassword } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Button from '$lib/components/Button.svelte';
  import PasswordField from '$lib/components/PasswordField.svelte';
  import Card from '$lib/components/Card.svelte';

  let {
    forcePasswordChange = false,
  }: {
    forcePasswordChange?: boolean;
  } = $props();

  const forced = $derived(forcePasswordChange || Boolean(session.user?.mustChangePassword));

  let currentPassword = $state('');
  let newPassword = $state('');
  let confirmPassword = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);
  let ok = $state<string | null>(null);

  const user = $derived(session.user as SessionUser | null);

  async function onSubmit(e: Event) {
    e.preventDefault();
    error = null;
    ok = null;

    if (!currentPassword || !newPassword) {
      error = 'Current and new password are required.';
      return;
    }
    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      error = `New password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
      return;
    }
    if (newPassword !== confirmPassword) {
      error = 'New password and confirmation do not match.';
      return;
    }
    if (newPassword === currentPassword) {
      error = 'New password must be different from the current password.';
      return;
    }

    busy = true;
    try {
      const res = await changePassword({ currentPassword, newPassword });
      currentPassword = '';
      newPassword = '';
      confirmPassword = '';
      ok = 'Password changed — sign in again with your new password.';
      // API cleared cookie and destroyed all sessions
      if (res.reauth !== false) {
        session.markPendingWelcomeAfterPasswordChange();
        await session.logout();
      }
    } catch (err) {
      error =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Password change failed';
    } finally {
      busy = false;
    }
  }
</script>

<div class="page">
  {#if !forced}
    <a class="back" href="#/settings">← Settings</a>
  {/if}
  <header class="head">
    <div>
      <p class="eyebrow">Security</p>
      <h2>Account</h2>
      <p class="lede">View your profile and change your password.</p>
    </div>
  </header>

  {#if forced}
    <p class="banner force" role="alert">
      You must set a new password before continuing. After you save, you will be signed out and can sign in with the new password.
    </p>
  {/if}

  <div class="grid" class:force-pw={forced}>
    <Card title="Profile" subtitle="Signed-in session">
      {#if user}
        <dl class="profile">
          <div>
            <dt>Name</dt>
            <dd>{user.displayName}</dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd class="mono">{user.email}</dd>
          </div>
          <div>
            <dt>Role</dt>
            <dd><Badge tone={user.role}>{user.role}</Badge></dd>
          </div>
          <div>
            <dt>Tenant</dt>
            <dd>{user.tenantName}</dd>
          </div>
        </dl>
      {:else}
        <p class="muted">Not signed in.</p>
      {/if}
    </Card>

    <Card
      title="Change password"
      subtitle={forced
        ? 'Required before you can use the portal — at least 6 characters'
        : 'At least 6 characters — signs you out on all devices'}
    >
      <form class="form" onsubmit={onSubmit}>
        {#if !forced}
          <label class="field">
          <span>Current password</span>
          <PasswordField
              bind:value={currentPassword}
              autocomplete="current-password"
              disabled={busy}
              required
            />
        </label>
          {/if}
        <label class="field">
          <span>New password</span>
          <PasswordField
              bind:value={newPassword}
              autocomplete="new-password"
              disabled={busy}
              required
              minlength={PASSWORD_MIN_LENGTH}
            />
        </label>
        <label class="field">
          <span>Confirm new password</span>
          <PasswordField
              bind:value={confirmPassword}
              autocomplete="new-password"
              disabled={busy}
              required
              minlength={PASSWORD_MIN_LENGTH}
            />
        </label>

        {#if error}
          <p class="banner error" role="alert">{error}</p>
        {/if}
        {#if ok}
          <p class="banner ok" role="status">{ok}</p>
        {/if}

        <div class="actions">
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? 'Updating…' : 'Update password'}
          </Button>
        </div>
      </form>
    </Card>
  </div>
</div>

<style>
  .page {
    display: grid;
    gap: var(--space-5, 1.25rem);
  }

  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .lede {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
    max-width: 40rem;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
    gap: var(--space-5, 1.25rem);
    align-items: start;
  }

  .profile {
    margin: 0;
    display: grid;
    gap: 0.85rem;
  }

  .profile div {
    display: grid;
    gap: 0.2rem;
  }

  dt {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
  }

  dd {
    margin: 0;
    font-size: var(--text-sm, 0.95rem);
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 0.9em;
  }

  .form {
    display: grid;
    gap: 0.85rem;
  }

  .field {
    display: grid;
    gap: 0.3rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .field span {
    color: var(--text-muted);
    font-weight: 500;
  }

  .field input {
    border: 1px solid var(--border);
    border-radius: 0.5rem;
    padding: 0.5rem 0.65rem;
    background: var(--surface);
    color: inherit;
    font: inherit;
  }

  .actions {
    display: flex;
    justify-content: flex-start;
  }

  .banner {
    margin: 0;
    padding: 0.65rem 0.75rem;
    border-radius: 0.5rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
    background: color-mix(in srgb, var(--danger, #ef4444) 10%, transparent);
  }

  .banner.ok {
    color: var(--success, #047857);
    background: color-mix(in srgb, var(--success, #10b981) 12%, transparent);
  }

  .banner.force {
    color: #92400e;
    background: color-mix(in srgb, #f59e0b 18%, transparent);
    border: 1px solid color-mix(in srgb, #f59e0b 45%, transparent);
    font-weight: 600;
  }

  .grid.force-pw {
    /* Password form first on small screens when forced */
    grid-template-columns: 1fr;
  }

  @media (min-width: 48rem) {
    .grid.force-pw {
      grid-template-columns: 1fr 1.2fr;
    }
  }

  .muted {
    color: var(--text-muted);
    margin: 0;
  }
</style>
